"use client";

import React from "react";
import { cn } from "@/lib/utils";

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?:
    | "primary"
    | "outline"
    | "danger"
    | "dark"
    | "soft"
    | "light"
    | "gray";
  size?: "sm" | "md" | "lg" | "xs" | "xss";
  color?: "purple" | "orange" | "red" | "green" | "blue";
  loading?: boolean;
};

export default function Button({
  variant = "primary",
  size = "md",
  color,
  loading = false,
  className,
  children,
  disabled,
  ...props
}: ButtonProps) {
  const base =
    "inline-flex gap-2 text-base items-center justify-center rounded-[12px] cursor-pointer font-medium transition-colors focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed";

  const variants = {
    primary: "bg-[var(--blue-500)] text-white hover:bg-[var(--blue-600)]",

    danger: "bg-gray-200 text-red-500 hover:bg-gray-300",

    dark: "bg-black text-white hover:bg-gray-800",

    soft: "bg-[var(--background)] text-[var(--gray-500)] border border-[var(--gray-100)] hover:bg-[var(--gray-50)]",

    light:
      "bg-[var(--background)] text-[var(--gray-800)] hover:bg-[var(--gray-50)]",

    gray: "bg-[var(--gray-50)] text-gray-500 hover:bg-[var(--gray-100)] p-1",

    outline: "bg-white border px-6 py-2 text-sm rounded-full",
  };

  const pillColors = {
    purple: "bg-purple-50 border border-purple-300 text-purple-600",
    orange: "bg-orange-50 border border-orange-300 text-orange-600",
    red: "bg-red-50 border border-red-300 text-red-600",
    green:
      "bg-[var(--green-50)] border border-[var(--green-500)] text-[var(--green-600)]",
    blue: "bg-blue-50 border border-blue-300 text-blue-600",
  };

  const sizes = {
    sm: "px-4 py-1 text-sm",
    md: "px-6 py-2 text-sm",
    lg: "px-8 py-3 text-base",
    xs: "px-2 py-1 text-xs",
    xss: "px-0 py-0 text-xxs",
  };

  return (
    <button
      disabled={loading || disabled}
      className={cn(
        base,
        variants[variant],
        variant === "outline" && color && pillColors[color],
        variant !== "outline" && sizes[size],
        className,
      )}
      {...props}
    >
      {children}
      {loading && (
        <span className="h-4 w-4 shrink-0 border-2 border-current border-t-transparent rounded-full animate-spin" />
      )}
    </button>
  );
}
